import React, { useState, useEffect, useRef } from 'react';
import { Shield, Eye, Heart, Award, CheckCircle } from 'lucide-react';
import { mockData } from '../mock';

const About = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const values = [
    { icon: Shield, title: 'Missão', content: mockData.about.mission },
    { icon: Eye, title: 'Visão', content: mockData.about.vision },
    { icon: Heart, title: 'Valores', content: mockData.about.values }
  ];

  return (
    <section id="about" ref={sectionRef} className="py-24 bg-gradient-to-b from-gray-900 to-black relative overflow-hidden">
      {/* Background Effects */}
      <div className="absolute inset-0">
        <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-l from-cyan-500/10 to-transparent rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-gradient-to-r from-blue-500/10 to-transparent rounded-full blur-3xl" />
      </div>

      <div className="container mx-auto px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <div className={`text-center mb-16 ${isVisible ? 'animate-fadeInUp' : 'opacity-0'}`}>
          <div className="inline-flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-cyan-500/20 to-blue-500/20 border border-cyan-400/30 rounded-full glass-effect mb-6">
            <Award className="w-4 h-4 text-cyan-400" />
            <span className="text-sm font-medium text-cyan-400">Sobre a NefilinSEC</span>
          </div>
          
          <h2 className="heading-1 mb-6">
            <span className="bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent">
              {mockData.about.title}
            </span>
          </h2>
          
          <p className="body-large text-gray-300 max-w-4xl mx-auto leading-relaxed">
            {mockData.about.description}
          </p>
        </div>

        {/* Main Content Grid */}
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left Side - Image */}
          <div className={`relative ${isVisible ? 'animate-fadeInLeft' : 'opacity-0'}`}>
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1599949104055-2d04026aee1e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzR8MHwxfHNlYXJjaHwxfHxuZXR3b3JrJTIwc2VjdXJpdHklMjBzZXJ2ZXJ8ZW58MHx8fHwxNzU3Mzc3NTg2fDA&ixlib=rb-4.1.0&q=85"
                alt="Infraestrutura de Segurança"
                className="w-full h-96 object-cover rounded-lg"
              />
              
              {/* Overlay Effect */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent rounded-lg" />
              
              {/* Floating Elements */}
              <div className="absolute -top-4 -right-4 w-20 h-20 glass-effect border border-cyan-400/30 rounded-full flex items-center justify-center animate-pulse">
                <Shield className="w-10 h-10 text-cyan-400" />
              </div>
              
              <div className="absolute -bottom-4 -left-4 w-16 h-16 glass-effect border border-blue-500/30 rounded-full flex items-center justify-center animate-pulse" style={{ animationDelay: '1s' }}>
                <CheckCircle className="w-8 h-8 text-blue-500" />
              </div>
            </div>
          </div>

          {/* Right Side - Values Cards */}
          <div className={`space-y-6 ${isVisible ? 'animate-fadeInRight' : 'opacity-0'}`}>
            {values.map((item, index) => {
              const IconComponent = item.icon;
              
              return (
                <div
                  key={index}
                  className="group p-6 glass-effect border border-white/10 rounded-lg hover:border-cyan-400/50 transition-all duration-500"
                  style={{ animationDelay: `${index * 0.2}s` }}
                >
                  <div className="flex items-start space-x-4">
                    {/* Icon */}
                    <div className="flex-shrink-0">
                      <div className="w-12 h-12 glass-effect border border-cyan-400/30 rounded-full flex items-center justify-center group-hover:border-cyan-400 group-hover:scale-110 transition-all duration-500">
                        <IconComponent className="w-6 h-6 text-cyan-400" />
                      </div>
                    </div>
                    
                    {/* Content */}
                    <div className="flex-1">
                      <h3 className="heading-3 text-white mb-3 group-hover:text-cyan-400 transition-colors duration-300">
                        {item.title}
                      </h3>
                      <p className="body-medium text-gray-300 leading-relaxed">
                        {item.content}
                      </p>
                    </div>
                  </div>

                  {/* Hover Effect Border */}
                  <div className="h-1 w-0 bg-gradient-to-r from-cyan-400 to-blue-500 rounded-full mt-4 group-hover:w-full transition-all duration-500" />
                </div>
              );
            })}
          </div>
        </div>

        {/* Compliance Badges */}
        <div className={`mt-20 text-center ${isVisible ? 'animate-fadeInUp' : 'opacity-0'}`} style={{ animationDelay: '0.8s' }}>
          <h3 className="heading-3 text-white mb-8">
            Conformidade com <span className="text-cyan-400">Frameworks Internacionais</span>
          </h3>
          
          <div className="flex flex-wrap justify-center gap-8">
            {['OWASP', 'MITRE ATT&CK', 'NIST', 'ISO 27001'].map((framework, index) => (
              <div
                key={index}
                className="px-6 py-3 glass-effect border border-cyan-400/30 rounded-full hover:border-cyan-400 hover:scale-105 transition-all duration-300"
              >
                <span className="text-cyan-400 font-semibold">{framework}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;